import { UIIcon } from '../../../config/icons';
import { $vars } from '../../../config/vars';
import { $on, $trigger, YBlurEvent, YEventHandlers, YEventSource, YFocusEvent, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { YInput } from '../../y/form';
import { y_language_names, yInterpolate, YLanguage, yLanguageCompare } from '../../y/i18n';
import { yIsEmptyObject } from '../../y/object';
import { $el } from '../elements';
import { UIClassesSpec, UIRelPos, UIValue } from '../types';
import { UIView, UIViewSpec } from '../view';
import { UIButton } from './button';
import { UIDropdown, valueDropdownGenerator } from './dropdown';
import { UISegmented } from './segmented';

export interface UII18NInputOptions {
    button_classes?: UIClassesSpec;
    classes?: UIClassesSpec;
    translator?: (text: string, src: YLanguage, dst: YLanguage) => Promise<string>;
    required?: boolean;
}

type UII18NChildInput<Value> = UIView & YInput<Value> & YEventSource<YBlurEvent & YFocusEvent>;

export class UII18NInput<Value extends string | undefined> extends UIView implements YInput<{ [lang in YLanguage]?: Value } | undefined> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<{ [lang in YLanguage]?: Value } | undefined> & YBlurEvent & YFocusEvent>;

    private _segmented: UISegmented<YLanguage>
    private _value: { [lang in YLanguage]?: Value };
    private _input: UII18NChildInput<Value>;
    private _language: YLanguage | undefined;
    private _recalcSegmented: () => void;
    private _recalcButtons: () => void;
    private _required: boolean;
    private _dont_commit?: boolean;

    okay!: boolean;
    disabled!: boolean;
    dirty!: boolean;

    constructor(input: UII18NChildInput<Value>, options: UII18NInputOptions = {}) {
        const supported_languages = $vars.supported_languages;

        const makeDropdown = (languages: YLanguage[], anchor: UIViewSpec, text: string, on_language: (l: YLanguage) => void) => {
            const popup = new UIDropdown<UIValue<YLanguage>>(languages.map(l => ({ key: l, name: yInterpolate(text, y_language_names[l]) })), valueDropdownGenerator)
            popup.on_choose = v => on_language(v.key);
            popup.open(anchor, { position: UIRelPos.Below | UIRelPos.End, adjust_size: true, match_size: true, capture_mouse: true });
        }

        const segmented = new UISegmented<YLanguage>([], { kind: 'k-under', classes: 'm-compact' });
        const add_button = new UIButton({
            icon: UIIcon.PLUS, classes: 'r-plus', on_click: () => {
                const present_languages = (Object.keys(this._value) as YLanguage[]);
                makeDropdown(supported_languages.filter(l => !present_languages.includes(l)), add_button, '$1$', l => {
                    this._addLanguage(l)
                });
            }
        });
        const translate_button = new UIButton({
            icon: UIIcon.WAND_MAGIC_SPARKLES, on_click: () => {
                makeDropdown(supported_languages.filter(l => l != this._language), translate_button, 'Translate to $1$', l => {
                    const text = input.value;
                    const src = this._language;
                    if(text) {
                        options.translator!(text, this._language!, l).then(tr => {
                            if(text == input.value && src == this._language)
                                this._addLanguage(l, tr);
                        })
                    }
                });
            }
        });

        const copy_button = new UIButton({
            icon: UIIcon.COPY, on_click: () => {
                makeDropdown(supported_languages.filter(l => l != this._language), copy_button, 'Copy to $1$', l => {
                    this._addLanguage(l, input.value);
                });
            }
        });

        const remove_button = new UIButton({
            icon: UIIcon.TRASH_CAN, on_click: () => {
                this._removeLanguage(this._language!);
            }
        });

        super('div', ['ui-i18n-input', options.classes], [
            $el.DIV('r-top', [segmented, add_button, $el.S(), translate_button, copy_button, remove_button]),
            input
        ]);

        this._segmented = segmented;
        this._required = !!options.required;

        this._recalcSegmented = () => {
            segmented.values = (Object.keys(this._value) as YLanguage[]).sort(yLanguageCompare).map(l => ({ key: l, name: y_language_names[l] }));
        }

        this._recalcButtons = () => {
            const present_languages = (Object.keys(this._value) as YLanguage[]);
            add_button.ui_show(undefined, !!supported_languages.filter(l => !present_languages.includes(l)).length);
            translate_button.ui_show(undefined, !!(options.translator && this._language) && supported_languages.length > 1);
            copy_button.ui_show(undefined, !!this._language && supported_languages.length > 1);
            remove_button.ui_show(undefined, !!this._language);

            this._input.disabled = !this._language;
            if(!this._language)
                this._input.value = '' as Value;
        }

        this.disabled = false;
        this._input = input;
        this._value = {};

        $on(segmented, 'value', l => {
            if(this._language && !this._dont_commit) {
                const v = input.value || undefined;
                if(v !== this._value[this._language]) {
                    this._value[this._language] = v;
                    $trigger(this, 'value', this._value);
                }
            }

            this._language = l;

            if(this._language) {
                input.value = this._value[this._language]!;
            }

            this._recalcButtons();
        });

        if(this._required) {
            $on(input, 'value', _v => { // TODO: actually use v if we make sure it's safe
                this.setOkay(!!(this._language && input.value) || Object.keys(this._value).some(k => this._value[k as YLanguage]));
            });
        } else {
            this.okay = true;
        }

        $on(input, 'blur', () => {
            $trigger(this, 'blur');
        });

        $on(input, 'focus', () => {
            $trigger(this, 'focus');
        });

        this._recalcSegmented();
        this._recalcButtons();
    }

    get value(): { [lang in YLanguage]?: Value } | undefined {
        if(this._language)
            this._value[this._language] = this._input.value || undefined;
        return yIsEmptyObject(this._value) ? undefined : this._value;
    }

    set value(v: { [lang in YLanguage]?: Value } | undefined) {
        this._value = v || {};
        this._dont_commit = true;
        this._recalcSegmented();
        if(!this._language || !(this._language in this._value)) {
            this._segmented.value = this._segmented.values?.[0]?.key;
        }
        this._dont_commit = false;

        if(this._language)
            this._input.value = this._value[this._language] || ('' as Value);
        this._recalcButtons();
    }

    private _addLanguage(l: YLanguage, v?: string) {
        if(l in this._value && !v) return;
        this._value[l] = (v || '') as Value;
        this._recalcSegmented();
        this._segmented.value = l;
    }

    private _removeLanguage(l: YLanguage) {
        if(!(l in this._value)) return;
        delete this._value[l];
        if(this._language == l)
            this._language = undefined;
        this._recalcSegmented();
        if(!this._language || !(this._language in this._value)) {
            this._segmented.value = this._segmented.values?.[0]?.key;
            this._recalcButtons();
        }
    }

    private setOkay(o: boolean) {
        if(o === this.okay) return;
        this.okay = o;
        $trigger(this, 'state');
    }

    markInvalid(focus: boolean) {
        if(this.okay) return false;
        return this._input.markInvalid?.(focus, true) || false;
    }
}

export class UINewI18NInput<Value extends string | undefined> extends UIView implements YInput<{ [lang in YLanguage]?: Value } | undefined> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<{ [lang in YLanguage]?: Value } | undefined> & YBlurEvent & YFocusEvent>;

    private _segmented: UISegmented<YLanguage>
    private _value: { [lang in YLanguage]?: Value };
    private _input: UII18NChildInput<Value>;
    private _language: YLanguage | undefined;
    private _recalcSegmented: () => void;
    private _recalcButtons: () => void;
    private _required: boolean;
    private _dont_commit?: boolean;

    okay!: boolean;
    disabled!: boolean;
    dirty!: boolean;

    constructor(input: UII18NChildInput<Value>, options: UII18NInputOptions = {}) {
        const supported_languages = $vars.supported_languages;

        const makeDropdown = (languages: YLanguage[], anchor: UIViewSpec, text: string, on_language: (l: YLanguage) => void) => {
            const popup = new UIDropdown<UIValue<YLanguage>>(languages.map(l => ({ key: l, name: yInterpolate(text, y_language_names[l]) })), valueDropdownGenerator)
            popup.on_choose = v => on_language(v.key);
            popup.open(anchor, { position: UIRelPos.Below | UIRelPos.End, adjust_size: true, match_size: true, capture_mouse: true });
        }

        const segmented = new UISegmented<YLanguage>([], { kind: 'k-tabs' });
        const add_button = new UIButton({
            icon: UIIcon.PLUS, classes: [options.button_classes, 'r-plus'], on_click: () => {
                const present_languages = (Object.keys(this._value) as YLanguage[]);
                makeDropdown(supported_languages.filter(l => !present_languages.includes(l)), add_button, '$1$', l => {
                    this._addLanguage(l)
                });
            }
        });
        const translate_button = new UIButton({
            icon: UIIcon.WAND_MAGIC_SPARKLES, classes: options.button_classes, on_click: () => {
                makeDropdown(supported_languages.filter(l => l != this._language), translate_button, 'Translate to $1$', l => {
                    const text = input.value;
                    const src = this._language;
                    if(text) {
                        options.translator!(text, this._language!, l).then(tr => {
                            if(text == input.value && src == this._language)
                                this._addLanguage(l, tr);
                        })
                    }
                });
            }
        });

        const copy_button = new UIButton({
            icon: UIIcon.COPY, classes: options.button_classes, on_click: () => {
                makeDropdown(supported_languages.filter(l => l != this._language), copy_button, 'Copy to $1$', l => {
                    this._addLanguage(l, input.value);
                });
            }
        });

        const remove_button = new UIButton({
            icon: UIIcon.TRASH_CAN, classes: options.button_classes, on_click: () => {
                this._removeLanguage(this._language!);
            }
        });

        super('div', ['ui-i18n-input', 'm-new', 'i-input', options.classes], [
            $el.DIV('r-top', [segmented, add_button, $el.S(), translate_button, copy_button, remove_button]),
            input
        ]);

        this._segmented = segmented;
        this._required = !!options.required;

        this._recalcSegmented = () => {
            segmented.values = (Object.keys(this._value) as YLanguage[]).sort(yLanguageCompare).map(l => ({ key: l, name: y_language_names[l] }));
        }

        this._recalcButtons = () => {
            const present_languages = (Object.keys(this._value) as YLanguage[]);
            add_button.ui_show(undefined, !!supported_languages.filter(l => !present_languages.includes(l)).length);
            translate_button.ui_show(undefined, !!(options.translator && this._language) && supported_languages.length > 1);
            copy_button.ui_show(undefined, !!this._language && supported_languages.length > 1);
            remove_button.ui_show(undefined, !!this._language);

            this._input.disabled = !this._language;
            if(!this._language)
                this._input.value = '' as Value;
        }

        this.disabled = false;
        this._input = input;
        this._value = {};

        $on(segmented, 'value', l => {
            if(this._language && !this._dont_commit) {
                const v = input.value || undefined;
                if(v !== this._value[this._language]) {
                    this._value[this._language] = v;
                    $trigger(this, 'value', this._value);
                }
            }

            this._language = l;

            if(this._language) {
                input.value = this._value[this._language]!;
            }

            this._recalcButtons();

            this.classList.toggle('f-first-selected', segmented.selected_index == 0);
        });

        if(this._required) {
            $on(input, 'value', _v => { // TODO: actually use v if we make sure it's safe
                this.setOkay(!!(this._language && input.value) || Object.keys(this._value).some(k => this._value[k as YLanguage]));
            });
        } else {
            this.okay = true;
        }

        $on(input, 'blur', () => {
            $trigger(this, 'blur');
        });

        $on(input, 'focus', () => {
            $trigger(this, 'focus');
        });

        this._recalcSegmented();
        this._recalcButtons();
    }

    get value(): { [lang in YLanguage]?: Value } | undefined {
        if(this._language)
            this._value[this._language] = this._input.value || undefined;
        return yIsEmptyObject(this._value) ? undefined : this._value;
    }

    set value(v: { [lang in YLanguage]?: Value } | undefined) {
        this._value = v || {};
        this._dont_commit = true;
        this._recalcSegmented();
        if(!this._language || !(this._language in this._value)) {
            this._segmented.value = this._segmented.values?.[0]?.key;
        }
        this._dont_commit = false;

        if(this._language)
            this._input.value = this._value[this._language] || ('' as Value);
        this._recalcButtons();
    }

    private _addLanguage(l: YLanguage, v?: string) {
        if(l in this._value && !v) return;
        this._value[l] = (v || '') as Value;
        this._recalcSegmented();
        this._segmented.value = l;
    }

    private _removeLanguage(l: YLanguage) {
        if(!(l in this._value)) return;
        delete this._value[l];
        if(this._language == l)
            this._language = undefined;
        this._recalcSegmented();
        if(!this._language || !(this._language in this._value)) {
            this._segmented.value = this._segmented.values?.[0]?.key;
            this._recalcButtons();
        }
    }

    private setOkay(o: boolean) {
        if(o === this.okay) return;
        this.okay = o;
        $trigger(this, 'state');
    }

    markInvalid(focus: boolean) {
        if(this.okay) return false;
        return this._input.markInvalid?.(focus, true) || false;
    }
}
